import { Injectable, inject } from '@angular/core';
import { DsDialogService } from 'src/app/shared/components/ds-dialog/ds-dialog.component';
import { ViewModeService } from 'src/app/core/services/view-mode.service';
import {
  MobileViewPromptDialogComponent,
  MobileViewPromptResult,
} from './mobile-view-prompt.dialog';

/**
 * Ngưỡng "cỡ điện thoại" = Tailwind `md` (768px). < 768px → mời sang bản mobile;
 * >= 768px (tablet trở lên) giữ nguyên shell desktop.
 *
 * CHÚ Ý: đây KHÔNG phải alias `md` của `MediaWatcherService` — `appConfig.screens.md`
 * là 960px (giá trị cũ của theme Fuse), lệch hẳn với Tailwind. Chính sự lệch đó khiến
 * thanh bottom-nav mobile cũ bật lên ở mọi cửa sổ hẹp hơn 960px, tức là cả trên laptop.
 */
export const PHONE_MAX_WIDTH_PX = 768;

/** Gộp các sự kiện resize liên tiếp — kéo cửa sổ không được bắn popup liên tục. */
const RESIZE_DEBOUNCE_MS = 300;

/**
 * Mời người dùng chuyển sang giao diện mobile khi viewport co xuống cỡ điện thoại.
 *
 * Chống spam bằng BA lớp:
 *  1. **debounce 300ms** — kéo cửa sổ qua lại chỉ đánh giá một lần khi dừng tay;
 *  2. **latch** — mỗi "lần thu nhỏ" chỉ hỏi ĐÚNG MỘT LẦN; latch chỉ nạp lại khi viewport
 *     quay lên >= 768px (nên đóng bằng ESC/backdrop cũng không bị hỏi lại ngay);
 *  3. **ghi nhớ vĩnh viễn** — bấm "Ở lại giao diện desktop" ghi cờ vào localStorage
 *     (`ViewModeService.dismissPrompt()`), từ đó không hỏi lại nữa.
 *
 * Cờ ghi nhớ được XOÁ ở hai chỗ (xem `ViewModeService`):
 *  - `switchTo('mobile')` — người dùng tự chọn bản mobile (nút ở user dropdown hoặc popup này);
 *  - đăng xuất (`AuthService._clearAuthState`) — máy dùng chung thì người kế tiếp được hỏi lại.
 *
 * Vòng đời: `start()` gọi từ `AdminLayoutComponent` (shell desktop) nên popup có ở MỌI màn
 * desktop; `stop()` gọi khi shell bị huỷ. Shell `/mobile` và `/pos` không chạm tới service này.
 */
@Injectable({ providedIn: 'root' })
export class MobileViewPromptService {
  private readonly _viewMode = inject(ViewModeService);
  private readonly _dialog = inject(DsDialogService);

  /** Latch: true = được phép hỏi cho lần thu nhỏ hiện tại. */
  private _armed = true;
  private _dialogOpen = false;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _onViewportChange: (() => void) | null = null;

  /** Bắt đầu theo dõi viewport. Idempotent. */
  start(): void {
    if (this._onViewportChange !== null) {
      return;
    }
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return; // SSR / môi trường không có DOM
    }

    this._onViewportChange = () => {
      if (this._debounceTimer !== null) {
        clearTimeout(this._debounceTimer);
      }
      this._debounceTimer = setTimeout(() => {
        this._debounceTimer = null;
        this.evaluate();
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', this._onViewportChange);
    window.addEventListener('orientationchange', this._onViewportChange);

    // Đánh giá ngay: người dùng có thể mở app khi cửa sổ đã hẹp sẵn.
    this.evaluate();
  }

  /** Ngừng theo dõi + dọn timer. */
  stop(): void {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    if (this._onViewportChange !== null) {
      window.removeEventListener('resize', this._onViewportChange);
      window.removeEventListener('orientationchange', this._onViewportChange);
      this._onViewportChange = null;
    }
  }

  /** Quyết định có mở popup hay không (public để test được mà không cần bắn resize). */
  evaluate(): void {
    const width = typeof window !== 'undefined' ? window.innerWidth : Number.MAX_SAFE_INTEGER;

    if (width >= PHONE_MAX_WIDTH_PX) {
      this._armed = true; // nạp lại latch cho lần thu nhỏ kế tiếp
      return;
    }
    if (!this._armed || this._dialogOpen) {
      return;
    }
    if (this._viewMode.promptDismissed) {
      return;
    }

    this._armed = false; // latch NGAY, trước khi mở dialog
    this._open();
  }

  private _open(): void {
    this._dialogOpen = true;
    const ref = this._dialog.open<
      MobileViewPromptDialogComponent,
      undefined,
      MobileViewPromptResult
    >(MobileViewPromptDialogComponent, {
      position: 'center',
      width: '420px',
    });

    ref.afterClosed().subscribe((result) => {
      this._dialogOpen = false;
      if (result === 'mobile') {
        this._viewMode.switchTo('mobile');
        return;
      }
      if (result === 'stay') {
        this._viewMode.dismissPrompt();
      }
      // undefined (ESC / backdrop): không ghi nhớ — latch đã tắt cho lần thu nhỏ này.
    });
  }
}
